import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { DollarSign, Printer, Receipt, AlertTriangle, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MyFeesPage() {
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const printRef = useRef();

  useEffect(() => {
    fetchStatement();
  }, []);

  const fetchStatement = async () => {
    try {
      const res = await axios.get(`${API}/fees/my-statement`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setStatement(res.data);
    } catch (err) {
      toast.error("Failed to load financial statement");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Financial Statement - ${statement?.student?.name || "Student"}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 40px; color: #1e293b; }
          .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #e2e8f0; padding-bottom: 20px; }
          .header h1 { font-size: 22px; color: #0f172a; }
          .header p { font-size: 13px; color: #64748b; margin-top: 4px; }
          .student-info { display: flex; justify-content: space-between; margin-bottom: 24px; padding: 16px; background: #f8fafc; border-radius: 8px; }
          .student-info div { }
          .student-info .label { font-size: 11px; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; }
          .student-info .value { font-size: 15px; font-weight: 600; color: #0f172a; }
          .summary { display: flex; gap: 20px; margin-bottom: 24px; }
          .summary-card { flex: 1; padding: 16px; border: 1px solid #e2e8f0; border-radius: 8px; text-align: center; }
          .summary-card .amount { font-size: 24px; font-weight: 700; }
          .summary-card .label { font-size: 12px; color: #64748b; }
          .fee-amount { color: #0f172a; }
          .paid-amount { color: #059669; }
          .balance-amount { color: #dc2626; }
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th { background: #f1f5f9; padding: 10px 12px; text-align: left; font-size: 12px; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e2e8f0; }
          td { padding: 10px 12px; border-bottom: 1px solid #f1f5f9; font-size: 13px; }
          .amount-col { font-family: monospace; font-weight: 600; }
          .status-badge { display: inline-block; padding: 2px 10px; border-radius: 12px; font-size: 11px; font-weight: 600; }
          .status-paid { background: #d1fae5; color: #059669; }
          .status-partial { background: #fef3c7; color: #d97706; }
          .status-pending { background: #fee2e2; color: #dc2626; }
          .footer { margin-top: 40px; text-align: center; font-size: 11px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 16px; }
          @media print { body { padding: 20px; } }
        </style>
      </head>
      <body>
        ${content.innerHTML}
        <div class="footer">
          <p>This is a computer-generated statement. Printed on ${new Date().toLocaleDateString()}.</p>
          <p>Cresabel School &mdash; Financial Statement</p>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  if (!statement) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <Card className="max-w-md w-full border-slate-200">
          <CardContent className="text-center py-12">
            <DollarSign size={48} className="mx-auto mb-4 text-slate-300" />
            <p className="text-slate-500">No financial information available</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { student, payments } = statement;
  const statusColor = student.fee_status === "Paid"
    ? "bg-emerald-50 text-emerald-700"
    : student.fee_status === "Partial"
    ? "bg-amber-50 text-amber-700"
    : "bg-red-50 text-red-700";

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-heading flex items-center gap-2">
            <Receipt className="text-pink-500" size={28} />
            My Financial Statement
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            View your fees and payment history
          </p>
        </div>
        <Button onClick={handlePrint} variant="outline" className="gap-2">
          <Printer size={16} />
          Print Statement
        </Button>
      </div>

      {/* Printable content */}
      <div ref={printRef}>
        <div className="header" style={{ display: "none" }}>
          <h1>Cresabel School - Financial Statement</h1>
        </div>

        {/* Student Info + Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="border-slate-200 md:col-span-3">
            <CardContent className="py-4">
              <div className="student-info flex flex-wrap gap-6 justify-between">
                <div>
                  <p className="label text-xs text-slate-500 uppercase">Student Name</p>
                  <p className="value text-lg font-bold text-slate-900">{student.name}</p>
                </div>
                <div>
                  <p className="label text-xs text-slate-500 uppercase">Student ID</p>
                  <p className="value text-lg font-bold text-slate-900">{student.student_id_code}</p>
                </div>
                <div>
                  <p className="label text-xs text-slate-500 uppercase">Class</p>
                  <p className="value text-lg font-bold text-slate-900">{student.class_name || "—"}</p>
                </div>
                <div>
                  <p className="label text-xs text-slate-500 uppercase">Fee Status</p>
                  <Badge className={`${statusColor} mt-1`}>{student.fee_status || "N/A"}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="summary grid grid-cols-3 gap-4 mb-6">
          <Card className="border-slate-200">
            <CardContent className="py-6 text-center">
              <p className="text-sm text-slate-500">Total Fees</p>
              <p className="text-2xl font-bold text-slate-900 font-mono fee-amount">
                ${student.total_fee.toFixed(2)}
              </p>
            </CardContent>
          </Card>
          <Card className="border-slate-200 border-l-4 border-l-emerald-500">
            <CardContent className="py-6 text-center">
              <p className="text-sm text-slate-500">Amount Paid</p>
              <p className="text-2xl font-bold text-emerald-600 font-mono paid-amount">
                ${student.amount_paid.toFixed(2)}
              </p>
            </CardContent>
          </Card>
          <Card className={`border-slate-200 border-l-4 ${student.balance <= 0 ? "border-l-emerald-500" : "border-l-red-500"}`}>
            <CardContent className="py-6 text-center">
              <p className="text-sm text-slate-500">Balance Due</p>
              <p className={`text-2xl font-bold font-mono balance-amount ${student.balance <= 0 ? "text-emerald-600" : "text-red-600"}`}>
                ${Math.max(0, student.balance).toFixed(2)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Status Banner */}
        {student.balance > 0 ? (
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 mb-6 flex items-center gap-3">
            <AlertTriangle size={20} className="text-amber-600 shrink-0" />
            <div>
              <p className="font-medium text-amber-700">Outstanding Balance: ${student.balance.toFixed(2)}</p>
              <p className="text-sm text-amber-600">Please make a payment to the school administration.</p>
            </div>
          </div>
        ) : (
          <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200 mb-6 flex items-center gap-3">
            <CheckCircle size={20} className="text-emerald-600 shrink-0" />
            <p className="font-medium text-emerald-700">All fees are paid. Thank you!</p>
          </div>
        )}

        {/* Payment History */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <DollarSign size={20} className="text-pink-500" />
              Payment History ({payments.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {payments.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <DollarSign size={40} className="mx-auto mb-3 opacity-50" />
                <p>No payments recorded yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50 hover:bg-slate-50">
                      <TableHead className="font-medium text-slate-500">#</TableHead>
                      <TableHead className="font-medium text-slate-500">Date</TableHead>
                      <TableHead className="font-medium text-slate-500">Amount</TableHead>
                      <TableHead className="font-medium text-slate-500">Method</TableHead>
                      <TableHead className="font-medium text-slate-500">Reference</TableHead>
                      <TableHead className="font-medium text-slate-500">Receipt</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((p, i) => (
                      <TableRow key={p.id}>
                        <TableCell className="text-slate-500">{i + 1}</TableCell>
                        <TableCell className="text-sm">
                          {new Date(p.payment_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="font-mono font-bold text-emerald-600 amount-col">
                          ${Number(p.amount).toFixed(2)}
                        </TableCell>
                        <TableCell>
                          {p.payment_method ? (
                            <Badge variant="secondary" className="bg-slate-100 text-xs">
                              {p.payment_method}
                            </Badge>
                          ) : "—"}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">
                          {p.payment_reference || "—"}
                        </TableCell>
                        <TableCell className="text-sm text-slate-600 font-mono">
                          {p.receipt_number || "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
